import { Texture, Rectangle, ImageSource, SCALE_MODES, WRAP_MODES } from 'pixi.js';

const TOLERANCE = 5;

export async function loadTextureWithTransparency(path: string): Promise<Texture> {
  const img = new Image();
  img.crossOrigin = 'anonymous';

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = path;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  const bgR = data[0];
  const bgG = data[1];
  const bgB = data[2];

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    if (
      Math.abs(r - bgR) <= TOLERANCE &&
      Math.abs(g - bgG) <= TOLERANCE &&
      Math.abs(b - bgB) <= TOLERANCE
    ) {
      data[i + 3] = 0;
    }
  }

  ctx.putImageData(imageData, 0, 0);

  const source = new ImageSource({
    resource: canvas,
    scaleMode: SCALE_MODES.NEAREST,
    wrapMode: WRAP_MODES.CLAMP,
  });
  return new Texture({ source });
}

/**
 * Load a GRID spritesheet (cols x rows) into per-frame textures, row-major.
 * Unlike loadSpriteSheetWithTransparency (vertical strip + colour-key), this is
 * for sheets that already carry an alpha channel (e.g. tank art): it preserves
 * the existing transparency and uses LINEAR scaling by default for smooth
 * (non-pixel) art. Used by tank sprites (render/tankSprites.ts).
 */
export async function loadGridSpriteSheet(
  path: string,
  opts: { cols: number; rows: number; frameCount?: number; smooth?: boolean },
): Promise<Texture[]> {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = path;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0);

  const source = new ImageSource({
    resource: canvas,
    scaleMode: opts.smooth === false ? SCALE_MODES.NEAREST : SCALE_MODES.LINEAR,
    wrapMode: WRAP_MODES.CLAMP,
  });

  const frameW = Math.floor(canvas.width / opts.cols);
  const frameH = Math.floor(canvas.height / opts.rows);
  const total = opts.frameCount ?? opts.cols * opts.rows;
  const textures: Texture[] = [];
  for (let i = 0; i < total; i++) {
    const col = i % opts.cols;
    const row = Math.floor(i / opts.cols);
    const frame = new Rectangle(col * frameW, row * frameH, frameW, frameH);
    textures.push(new Texture({ source, frame }));
  }
  return textures;
}

export async function loadSpriteSheetWithTransparency(
  path: string,
  frameHeight: number,
): Promise<Texture[]> {
  const img = new Image();
  img.crossOrigin = 'anonymous';

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = path;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  const bgR = data[0];
  const bgG = data[1];
  const bgB = data[2];

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    if (
      Math.abs(r - bgR) <= TOLERANCE &&
      Math.abs(g - bgG) <= TOLERANCE &&
      Math.abs(b - bgB) <= TOLERANCE
    ) {
      data[i + 3] = 0;
    }
  }

  ctx.putImageData(imageData, 0, 0);

  const source = new ImageSource({
    resource: canvas,
    scaleMode: SCALE_MODES.NEAREST,
    wrapMode: WRAP_MODES.CLAMP,
  });
  const textures: Texture[] = [];
  const frameCount = Math.floor(canvas.height / frameHeight);

  for (let i = 0; i < frameCount; i++) {
    const frame = new Rectangle(0, i * frameHeight, canvas.width, frameHeight);
    const texture = new Texture({ source, frame });
    textures.push(texture);
  }

  return textures;
}
